"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { walletIconProps } from "@/components/wallets/utils/walletIconParams";
import { usePassportProvider } from "@/context";
import { CheckoutProvider } from "@/context/CheckoutProvider";
import { Erc20Provider } from "@/context/Erc20Provider";
import { useRightNav } from "@/context/RightNavContext";
import { useFetchWallets } from "@/hooks";
import { useCollectionsQuery } from "@/hooks/useQuery";
import { useEffect, useMemo } from "react";
import { sortCollections, transformCollection } from "./data-transformers";
import { InventoryList } from "./inventory-list";
import type { InventorysListProps } from "./types";

export function InventorysList({ className }: InventorysListProps) {
  const { walletAddress, isLoggedIn, isInitializing } = usePassportProvider();
  const { data: linkedWallets } = useFetchWallets(isLoggedIn);
  const { selectedWallet, setSelectedWallet } = useRightNav();

  const handleWalletChange = (newWalletAddress: string) => {
    setSelectedWallet(newWalletAddress);
  };

  useEffect(() => {
    if (walletAddress && selectedWallet === "") {
      setSelectedWallet(walletAddress);
    }
  }, [walletAddress, selectedWallet, setSelectedWallet]);

  const shouldEnableQueries = !!selectedWallet && !isInitializing;
  const {
    zkEvmData,
    starkExData,
    zkEvmError,
    starkExError,
    zkEvmIsLoading,
    starkExIsLoading,
  } = useCollectionsQuery(selectedWallet, shouldEnableQueries);

  const { collections, error, isLoading } = useMemo(() => {
    const loading = isInitializing || zkEvmIsLoading || starkExIsLoading;
    const combinedError = zkEvmError || starkExError;

    if (loading || combinedError) {
      return {
        collections: [],
        error: combinedError,
        isLoading: loading,
      };
    }

    const zkEvmCollections =
      zkEvmData?.pages.flatMap((page) => page.collections) || [];
    const starkExCollections =
      starkExData?.pages.flatMap((page) => page.collections) || [];
    const allCollections = [...zkEvmCollections, ...starkExCollections];

    const transformedCollections = allCollections.map((collection) =>
      transformCollection(collection, selectedWallet),
    );

    const sortedCollections = sortCollections(transformedCollections);

    return {
      collections: sortedCollections,
      error: null,
      isLoading: false,
    };
  }, [
    zkEvmData,
    starkExData,
    zkEvmError,
    starkExError,
    zkEvmIsLoading,
    starkExIsLoading,
    selectedWallet,
    isInitializing,
  ]);

  const formatWalletAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  if (!isLoggedIn) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-white/40 text-xs">
          Please sign in to view inventory
        </div>
      </div>
    );
  }

  return (
    <Erc20Provider>
      <CheckoutProvider>
        <div className={`space-y-4 ${className}`}>
          {linkedWallets && linkedWallets.length > 0 && (
            <div className="space-y-2">
              <span className="text-white/60 text-xs">Wallet</span>

              <Select
                value={selectedWallet || ""}
                onValueChange={handleWalletChange}
              >
                <SelectTrigger className="h-8 text-xs bg-white/5 border-white/20 text-white">
                  <SelectValue placeholder="Select wallet">
                    {selectedWallet &&
                      linkedWallets &&
                      (() => {
                        const selectedWalletData = linkedWallets.find(
                          (w) => w.address === selectedWallet,
                        );
                        if (selectedWalletData) {
                          const iconProps = walletIconProps(
                            selectedWalletData.type,
                          );
                          return (
                            <div className="flex items-center gap-2">
                              <img
                                src={iconProps.imagePath}
                                alt={iconProps.alt}
                                className="w-4 h-4 object-contain rounded"
                              />
                              <span className="truncate">
                                {selectedWalletData.name ||
                                  selectedWalletData.type ||
                                  formatWalletAddress(selectedWallet)}
                              </span>
                            </div>
                          );
                        }
                        return "Select wallet";
                      })()}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-black/90 border-white/20">
                  {linkedWallets.map((wallet) => {
                    const iconProps = walletIconProps(wallet.type);
                    return (
                      <SelectItem
                        key={wallet.address}
                        value={wallet.address}
                        className="text-white hover:bg-white/10 focus:bg-white/10"
                      >
                        <div className="flex items-center justify-between w-full">
                          <div className="flex items-center gap-2">
                            <img
                              src={iconProps.imagePath}
                              alt={iconProps.alt}
                              className="w-4 h-4 object-contain rounded"
                            />
                            <span className="truncate">
                              {wallet.name ||
                                formatWalletAddress(wallet.address)}
                            </span>
                          </div>
                          <span className="text-white/60 text-xs ml-2">
                            {formatWalletAddress(wallet.address)}
                          </span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}
          <InventoryList
            collections={collections}
            isLoading={isLoading}
            error={error}
          />
        </div>
      </CheckoutProvider>
    </Erc20Provider>
  );
}

export { InventoryList } from "./inventory-list";
export { InventoryCollectionItem } from "./inventory-collection";
